/**
 * Phibian Virtual Workforce — Talk to Phoebe (VW-6, #379).
 *
 * Two halves of one screen, and the difference between them is the product:
 *
 *   LEFT  the conversation — what the client experiences: one employee,
 *         Phoebe, who says she is an AI. Over Tavus this is a face-to-face
 *         room; until Tavus credentials exist it is the text channel.
 *   RIGHT the workforce underneath — what the administrator needs to trust
 *         her: the case, the tasks, the workers, the approvals and the proof.
 *         EVERY LINE COMES FROM GET /api/virtual-workforce/cases/:id, which
 *         reads persisted facts, actions and approvals. Nothing here is
 *         inferred from the conversation.
 *
 * The transcript lives only in this tab (founder ruling D7): the system of
 * record is the facts, not what was said.
 */

import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Input, Pill } from '@cwos/ui-admin';
import { agents, virtualWorkforce, type virtualWorkforce as Vw } from '@cwos/api-client';
import { usePermissions } from '../permissions.js';
import { LoadFailure } from '../LoadFailure.js';
import { MIN_DECISION_REASON } from '../agents/AgentApprovals.js';

export const TALK_TO_PHOEBE_KEY = 'agents:read';
export const TALK_TO_PHOEBE_IS =
  'Talk to Phoebe is where you meet Phibian’s virtual intake coordinator and watch the governed ' +
  'work she does underneath the conversation — in a synthetic demo organization.';

interface Line {
  readonly who: 'phoebe' | 'client';
  readonly text: string;
  readonly guarded?: boolean;
}

const TASK_PILL: Record<
  Vw.TaskState,
  { variant: 'good' | 'look' | 'plain' | 'urgent' | 'sky'; mark: string; word: string }
> = {
  done: { variant: 'good', mark: '✓', word: 'Done' },
  in_progress: { variant: 'sky', mark: '…', word: 'Working' },
  waiting: { variant: 'look', mark: '○', word: 'Waiting' },
  not_started: { variant: 'plain', mark: '○', word: 'Not started' },
  declined: { variant: 'urgent', mark: '✕', word: 'Declined' },
};

const SOURCE_WORD: Record<Vw.CaseFact['sourceKind'], string> = {
  conversation: 'said by the client',
  tool: 'produced by a worker',
  human: 'decided by a person',
};

export function TalkToPhoebe() {
  const queryClient = useQueryClient();
  const { has } = usePermissions();
  const [session, setSession] = useState<Vw.StartedConversation | null>(null);
  const [lines, setLines] = useState<Line[]>([]);
  const [draft, setDraft] = useState('');
  const [ended, setEnded] = useState(false);
  const [reason, setReason] = useState('');

  const start = useMutation({
    mutationFn: () => virtualWorkforce.startConversation(),
    onSuccess: (started) => {
      setSession(started);
      setEnded(false);
      setLines([{ who: 'phoebe', text: started.greeting }]);
    },
  });

  const send = useMutation({
    mutationFn: (utterance: string) => virtualWorkforce.say(session!.sessionId, utterance),
    onMutate: (utterance) => setLines((l) => [...l, { who: 'client', text: utterance }]),
    onSuccess: (turn) => {
      setLines((l) => [...l, { who: 'phoebe', text: turn.reply, guarded: turn.guarded }]);
      void queryClient.invalidateQueries({ queryKey: ['virtual-workforce', 'case'] });
    },
  });

  const end = useMutation({
    mutationFn: () => virtualWorkforce.endConversation(session!.sessionId),
    onSuccess: () => setEnded(true),
  });

  const caseQuery = useQuery({
    queryKey: ['virtual-workforce', 'case', session?.caseId],
    queryFn: () => virtualWorkforce.getCase(session!.caseId),
    enabled: session !== null,
    refetchInterval: 3_000,
  });

  const decide = useMutation({
    mutationFn: ({
      approvalId,
      decision,
    }: {
      approvalId: string;
      decision: 'GRANTED' | 'DENIED';
    }) => agents.decideApproval(approvalId, decision, reason.trim()),
    onSuccess: () => {
      setReason('');
      void queryClient.invalidateQueries({ queryKey: ['virtual-workforce', 'case'] });
    },
  });
  // The approval door refuses a decision without a reason (agent-runtime routes.ts).
  const decisionBlocked = reason.trim().length < MIN_DECISION_REASON || decide.isPending;

  const view = caseQuery.data;
  const pending = view?.approvals.find((a) => a.status === 'PENDING');

  return (
    <article className="rn-col" style={{ gap: 'var(--space-16)' }}>
      <header className="rn-col" style={{ gap: 'var(--space-8)' }}>
        <h1>Talk to Phoebe</h1>
        <p className="rn-muted">
          Phoebe is Phibian’s virtual intake coordinator — an AI worker, not a person. Everything
          she tells a client comes from work Phibian has actually recorded. Synthetic demo data
          only.
        </p>
      </header>

      {!session ? (
        <section className="rn-card card-record">
          <button
            type="button"
            className="rn-btn"
            disabled={start.isPending}
            onClick={() => start.mutate()}
          >
            {start.isPending ? 'Connecting…' : 'Talk to Phoebe'}
          </button>
          {start.isError ? (
            <LoadFailure
              error={start.error}
              message="Phoebe could not start a conversation."
              onRetry={() => start.mutate()}
            />
          ) : null}
        </section>
      ) : (
        <div
          className="rn-row"
          style={{ gap: 'var(--space-16)', alignItems: 'flex-start', flexWrap: 'wrap' }}
        >
          {/* ===== THE CONVERSATION ============================================ */}
          <section
            className="rn-card card-record rn-col"
            style={{ gap: 'var(--space-12)', flex: '1 1 360px' }}
          >
            <h2 className="rn-card-title">Phoebe · Virtual Intake Coordinator</h2>
            {session.joinUrl ? (
              <iframe
                title="Phoebe"
                src={roomUrl(session.joinUrl, session.joinToken)}
                allow="camera; microphone; autoplay"
                style={{
                  width: '100%',
                  aspectRatio: '16 / 9',
                  border: 0,
                  borderRadius: 'var(--radius-12, 12px)',
                }}
              />
            ) : (
              <>
                <ol className="rn-list" aria-live="polite">
                  {lines.map((line, i) => (
                    <li key={i} className="rn-col" style={{ gap: 'var(--space-4)' }}>
                      <span className="rn-strong">
                        {line.who === 'phoebe' ? 'Phoebe' : 'Client'}
                      </span>
                      <span>{line.text}</span>
                      {line.guarded ? (
                        <span className="rn-muted">
                          Phoebe’s first wording claimed something Phibian had not verified; the
                          verified wording was used instead.
                        </span>
                      ) : null}
                    </li>
                  ))}
                </ol>
                <form
                  className="rn-row"
                  style={{ gap: 'var(--space-8)' }}
                  onSubmit={(e) => {
                    e.preventDefault();
                    const text = draft.trim();
                    if (!text || send.isPending || ended) return;
                    setDraft('');
                    send.mutate(text);
                  }}
                >
                  <span style={{ flex: 1 }}>
                    <Input
                      label="Speak as the client"
                      value={draft}
                      maxLength={1000}
                      disabled={ended}
                      onChange={(e) => setDraft(e.target.value)}
                      placeholder="e.g. My son needs services. We have Demo Health Plan."
                    />
                  </span>
                  <button type="submit" className="rn-btn" disabled={send.isPending || ended}>
                    {send.isPending ? 'Phoebe is working…' : 'Send'}
                  </button>
                </form>
                {send.isError ? (
                  <p className="rn-muted">That message did not go through. Try again.</p>
                ) : null}
              </>
            )}
            <button
              type="button"
              className="rn-btn-quiet"
              disabled={end.isPending || ended}
              onClick={() => end.mutate()}
            >
              {ended ? 'Conversation ended' : 'End conversation'}
            </button>
          </section>

          {/* ===== THE WORKFORCE UNDERNEATH ===================================== */}
          <section
            className="rn-card card-record rn-col"
            style={{ gap: 'var(--space-12)', flex: '1 1 360px' }}
          >
            <h2 className="rn-card-title">Virtual Workforce</h2>
            {caseQuery.isError ? (
              <LoadFailure
                error={caseQuery.error}
                message="We could not load the case."
                onRetry={() => void caseQuery.refetch()}
              />
            ) : !view ? (
              <p className="rn-muted">Loading the case…</p>
            ) : (
              <>
                <div className="rn-col" style={{ gap: 'var(--space-4)' }}>
                  <span className="rn-strong">
                    {view.worker.name} — {view.worker.role}
                  </span>
                  <span>Status: {view.worker.status}</span>
                  <span className="rn-muted">Current case: {view.case.subjectLabel}</span>
                </div>

                <h3>Tasks</h3>
                <ul className="rn-list">
                  {view.tasks.map((task) => {
                    const p = TASK_PILL[task.state];
                    return (
                      <li
                        key={task.key}
                        className="rn-row"
                        style={{ gap: 'var(--space-8)', alignItems: 'center' }}
                      >
                        <span aria-hidden>{p.mark}</span>
                        <span style={{ flex: 1 }}>
                          {task.label}
                          {task.detail ? <span className="rn-muted"> — {task.detail}</span> : null}
                        </span>
                        <Pill variant={p.variant}>{p.word}</Pill>
                        {task.evidence ? (
                          <span
                            className="rn-muted"
                            title={`${task.evidence.kind} ${task.evidence.id}`}
                          >
                            {task.evidence.kind} {task.evidence.id.slice(0, 8)}
                          </span>
                        ) : null}
                      </li>
                    );
                  })}
                </ul>

                {pending ? (
                  <div className="rn-card agent-approval-bar">
                    <p>An intake specialist must review this case before anything is final.</p>
                    {has('agents:approve') ? (
                      <>
                        <Input
                          label="Why"
                          hint="Required, and recorded with your decision."
                          value={reason}
                          maxLength={1000}
                          onChange={(e) => setReason(e.target.value)}
                        />
                        <div className="agent-approval-actions">
                          <button
                            type="button"
                            className="rn-btn"
                            disabled={decisionBlocked}
                            onClick={() =>
                              decide.mutate({ approvalId: pending.id, decision: 'GRANTED' })
                            }
                          >
                            Accept intake
                          </button>
                          <button
                            type="button"
                            className="rn-btn-quiet"
                            disabled={decisionBlocked}
                            onClick={() =>
                              decide.mutate({ approvalId: pending.id, decision: 'DENIED' })
                            }
                          >
                            Decline
                          </button>
                        </div>
                        {decide.isError ? (
                          <LoadFailure
                            error={decide.error}
                            message="That decision did not go through. Nothing has changed."
                            refusedHeading="You may not do that"
                          />
                        ) : null}
                      </>
                    ) : (
                      <p className="rn-muted">Deciding needs the agents:approve permission.</p>
                    )}
                  </div>
                ) : null}

                <h3>Agents involved</h3>
                <p>{view.agentsInvolved.join(' → ')}</p>

                <details>
                  <summary>Facts and where each came from ({view.facts.length})</summary>
                  <ul className="rn-list">
                    {view.facts.map((f) => (
                      <li key={f.id} className="rn-col">
                        <span className="rn-strong">{f.key}</span>
                        <code>{JSON.stringify(f.value)}</code>
                        <span className="rn-muted">
                          {SOURCE_WORD[f.sourceKind]} · {f.worker} ·{' '}
                          {provenanceSentence(f.provenance)}
                        </span>
                      </li>
                    ))}
                  </ul>
                </details>

                <details>
                  <summary>Actions ({view.actions.length})</summary>
                  <ul className="rn-list">
                    {view.actions.map((a) => (
                      <li key={a.id} className="rn-col">
                        <span className="rn-strong">
                          {a.worker}: {a.operation}
                        </span>
                        <span className="rn-muted">
                          {a.toolId} · risk {a.risk} · {a.result ?? 'not attempted'} · approval{' '}
                          {a.approvalStatus}
                        </span>
                      </li>
                    ))}
                  </ul>
                </details>

                <details>
                  <summary>Audit trail ({view.audit.length})</summary>
                  <ol className="rn-list">
                    {view.audit.map((a, i) => (
                      <li key={i}>
                        {a.action}{' '}
                        <span className="rn-muted">
                          · {a.actor === 'person' ? 'by a person' : 'by Phibian'} ·{' '}
                          <time dateTime={new Date(a.occurredAt).toISOString()}>
                            {new Date(a.occurredAt).toLocaleString()}
                          </time>
                        </span>
                      </li>
                    ))}
                  </ol>
                </details>

                <details>
                  <summary>Step by step ({view.timeline.length})</summary>
                  <ol className="rn-list">
                    {view.timeline.map((t) => (
                      <li key={t.seq}>{t.summary}</li>
                    ))}
                  </ol>
                </details>

                {view.run ? (
                  <a className="rn-link" href={`/agents/runs/${view.run.id}`}>
                    Open the full run record →
                  </a>
                ) : null}
              </>
            )}
          </section>
        </div>
      )}
    </article>
  );
}

/** One line of provenance: the governed record behind a fact, never a guess. */
export function provenanceSentence(p: Vw.FactProvenance): string {
  switch (p.kind) {
    case 'conversation':
      return `conversation turn ${p.turn.split(':').pop()}`;
    case 'action':
      return (
        `${p.toolId} · risk ${p.risk} · autonomy level ${p.autonomyLevel}` +
        (p.permissionChecked ? ` · checked ${p.permissionChecked}` : '') +
        ` · ${p.result ?? 'not attempted'}`
      );
    case 'approval':
      return `human decision ${p.status.toLowerCase()}${p.decidedAt ? ` at ${new Date(p.decidedAt).toLocaleString()}` : ''}`;
    case 'unresolved':
      return 'producing record not found';
  }
}

/** The private room URL with its meeting token, whatever query the URL already has. */
export function roomUrl(joinUrl: string, joinToken: string | null): string {
  const url = new URL(joinUrl);
  if (joinToken) url.searchParams.set('t', joinToken);
  return url.toString();
}
